import { useState } from "react"
import { Alert, App as AntdApp, Button, Divider, Form, Input, Modal } from "antd"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { MIN_PASSWORD_LENGTH, type StaffMe } from "@csbi/contracts"
import { changePassword, updateProfile } from "../../../features/tiles/api.js"
import { ApiError } from "../../../shared/api/request.js"

type Values = {
  username: string
  account: string
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
}

export type AccountSettingsProps = {
  open: boolean
  staff: StaffMe
  onClose: () => void
}

export function AccountSettings({ open, staff, onClose }: AccountSettingsProps) {
  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Account settings" destroyOnHidden width={460}>
      <SettingsBody key={`${staff.staffId}:${String(open)}`} staff={staff} onClose={onClose} />
    </Modal>
  )
}

function SettingsBody({ staff, onClose }: { staff: StaffMe; onClose: () => void }) {
  const [form] = Form.useForm<Values>()
  const [changing, setChanging] = useState(false)
  const [failure, setFailure] = useState<string>()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()

  const save = useMutation({
    mutationFn: async (v: Values) => {
      const profile: { username?: string; account?: string } = {}
      if (v.username !== staff.username) profile.username = v.username
      if (v.account !== staff.account) profile.account = v.account

      const next = Object.keys(profile).length > 0 ? await updateProfile(profile) : staff

      let closed = 0
      if (changing && v.currentPassword && v.newPassword) {
        const r = await changePassword({ currentPassword: v.currentPassword, newPassword: v.newPassword })
        closed = r.otherSessionsClosed
      }
      return { next, closed, changedPassword: changing }
    },
    onSuccess: ({ next, closed, changedPassword }) => {
      qc.setQueryData(["auth", "me"], next)
      void message.success(
        changedPassword
          ? closed > 0
            ? `Saved. Your password was changed, and ${closed} other session(s) were signed out.`
            : "Saved. Your password was changed."
          : "Saved.",
        5,
      )
      onClose()
    },
    onError: (err) => setFailure(err instanceof ApiError ? err.message : "Could not save. Please try again."),
  })

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={{ username: staff.username, account: staff.account }}
      onFinish={(v) => {
        setFailure(undefined)
        save.mutate(v)
      }}
      style={{ marginTop: 8 }}
    >
      {failure && <Alert type="error" showIcon message={failure} style={{ marginBottom: 16 }} />}

      <Form.Item
        name="username"
        label="Username"
        extra="Shown in the breadcrumb and on collection runs. It does not have to be unique."
        rules={[{ required: true, message: "Username cannot be empty" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="account"
        label="Account"
        extra="The email you sign in with. Must be unique — changing it changes how you sign in."
        rules={[
          { required: true, message: "Account cannot be empty" },
          { type: "email", message: "Account must be an email address" },
        ]}
      >
        <Input autoComplete="username" />
      </Form.Item>

      <Divider style={{ margin: "8px 0 16px" }} />

      {!changing ? (
        <Button onClick={() => setChanging(true)} style={{ marginBottom: 8 }}>
          Change password
        </Button>
      ) : (
        <>
          {/*
            * ⚠ The current password is required even though the person is already signed in — the session may
            * have been picked up on an unlocked machine by somebody else. The server verifies it too; this only
            * saves a pointless round trip.
            */}
          <Form.Item
            name="currentPassword"
            label="Current password"
            rules={[{ required: true, message: "Enter your current password" }]}
          >
            <Input.Password autoComplete="current-password" autoFocus />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New password"
            rules={[
              { required: true, message: "Enter a new password" },
              { min: MIN_PASSWORD_LENGTH, message: `At least ${MIN_PASSWORD_LENGTH} characters` },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Repeat the new password" },
              ({ getFieldValue }) => ({
                validator: (_r, value) =>
                  !value || getFieldValue("newPassword") === value
                    ? Promise.resolve()
                    : Promise.reject(new Error("The two passwords do not match")),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setChanging(false)}>
            Cancel password change
          </Button>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" htmlType="submit" loading={save.isPending}>
          Save
        </Button>
      </div>
    </Form>
  )
}
